/**
 * POST /auth/create_user
 */

import { A } from '@mobily/ts-belt';
import { z } from 'zod';

import { ZodPinCode } from '@Common/types/auth/reset_pin';
import {
  CountryCode,
  ZodTaxResidenceCodeNonProd,
  ZodTaxResidenceCodeProd,
} from '@Common/types/countries';
import {
  AUTH_ERROR_DEVICE_NOT_FOUND,
  AUTH_ERROR_EMAIL_ALREADY_TAKEN,
  AUTH_ERROR_EMAIL_NOT_FOUND,
  AUTH_ERROR_NOT_FOUND,
  AUTH_ERROR_USERNAME_ALREADY_TAKEN,
} from '@Common/types/error_messages';
import { ZodInvestmentExperience } from '@Common/types/InvestmentExperience';
import { ZodLanguageCode } from '@Common/types/language';
import { ZodId } from '@Common/types/request';
import { ZodUserBirthdate } from '@Common/types/user/birthdate';
import { ZodUserFirstName, ZodUserLastName } from '@Common/types/user/legal_name';
import { ZodAppUserEmail } from '@Common/types/zod/zod-email';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodHashedIdResponse } from '@Common/types/zod/zod-hash-id';
import { ZodPhoneNumber } from '@Common/types/zod/zod-phone-number';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';
import { ZodUsername } from '@Common/types/zod/zod-username';

import type { Locale } from '@Common/i18n/i18n';

export type AcquisitionChannelType =
  | 'referral'
  | 'tik_tok'
  | 'meta'
  | 'reddit'
  | 'linkedin'
  | 'twitter'
  | 'snapchat'
  | 'app_store'
  | 'shares_blog'
  | 'friends'
  | 'other'
  | 'instagram'
  | 'facebook'
  | 'youtube'
  | 'google'
  | 'recommendation'
  | 'tv_or_radio'
  | 'metro';

const AcquisitionChannels: AcquisitionChannelType[] = [
  'referral',
  'tik_tok',
  'meta',
  'reddit',
  'linkedin',
  'twitter',
  'snapchat',
  'app_store',
  'shares_blog',
  'friends',
  'other',
  'instagram',
  'facebook',
  'youtube',
  'google',
  'recommendation',
  'tv_or_radio',
  'metro',
];

export type AcquisitionChannel = {
  type: AcquisitionChannelType;
  translationKey: Locale;
  platform?: 'ios' | 'android';
};

export const ACQUISITION_CHANNELS: AcquisitionChannel[] = [
  {
    type: 'meta',
    translationKey: 'common.acquisition_channels.meta',
  },
  {
    type: 'tik_tok',
    translationKey: 'common.acquisition_channels.tik_tok',
  },
  {
    type: 'snapchat',
    translationKey: 'common.acquisition_channels.snapchat',
  },
  {
    type: 'linkedin',
    translationKey: 'common.acquisition_channels.linkedin',
  },
  {
    type: 'other',
    translationKey: 'common.acquisition_channels.other',
  },
];

export const ZodFriendRequest = z.object({ target_id: ZodId });
export type FriendRequest = { target_id: number };

export type PostCreateUserBody = z.input<typeof ZodCreateUserBodyBase> & {
  country_of_tax_residence: CountryCode;
};

export type PostCreateUserBodyOutput = z.output<typeof ZodCreateUserBodyBase> & {
  country_of_tax_residence: CountryCode;
};

export const ZodCreateUserResponse = z.object({
  id: ZodHashedIdResponse,
  token: ZodTrimmedString,
  device_token: ZodTrimmedString,
  display_name: ZodTrimmedString,
  refresh_token: ZodTrimmedString,
});

export const ZodCreateUserResponseBackend = z.object({
  ...ZodCreateUserResponse.omit({
    id: true,
  }).shape,
  id: ZodId,
});

export type PostCreateUserResponse = z.infer<typeof ZodCreateUserResponse>;

export type PostCreateUserResponseBackend = z.infer<typeof ZodCreateUserResponseBackend>;

export type PostCreateUserErrorResponse = {
  error:
    | typeof AUTH_ERROR_USERNAME_ALREADY_TAKEN
    | typeof AUTH_ERROR_EMAIL_ALREADY_TAKEN
    | typeof AUTH_ERROR_NOT_FOUND
    | typeof AUTH_ERROR_DEVICE_NOT_FOUND
    | typeof AUTH_ERROR_EMAIL_NOT_FOUND;
};

export const ZodCreateUserIsFollow = z.boolean().optional().default(false);
export const ZodCreateUserFriendRequests = z.array(ZodFriendRequest);
export const ZodCreateUserExclusiveInviteCode = ZodTrimmedString.length(4).optional();

export const ZodCreateUserBodyBase = z.object({
  firstname: ZodUserFirstName,
  lastname: ZodUserLastName,
  birthdate: ZodUserBirthdate,
  pin_code: ZodPinCode,
  username: ZodUsername.optional(),
  phone_number: ZodPhoneNumber,
  email: ZodAppUserEmail,
  email_marketing_consent: z.boolean(),
  confirmation_code: ZodTrimmedString,
  device_id: ZodTrimmedString,
  language: ZodLanguageCode,
  is_follow: ZodCreateUserIsFollow,
  investment_experience: ZodInvestmentExperience.optional(),
  exclusive_invite_code: ZodCreateUserExclusiveInviteCode,
  conflict_of_interest_policy_agreement: z.literal(true).optional(),
  acquisition_channel: z
    .enum([A.getUnsafe(AcquisitionChannels, 0), ...AcquisitionChannels])
    .optional(),
});

export const ZodCreateUserBodyProd = z.object({
  ...ZodCreateUserBodyBase.shape,
  country_of_tax_residence: ZodTaxResidenceCodeProd,
});

export const ZodCreateUserBodyNonProd = z.object({
  ...ZodCreateUserBodyBase.shape,
  country_of_tax_residence: ZodTaxResidenceCodeNonProd,
});

export const postAuthCreateUserEndpoint = endpoint({
  method: 'POST',
  body: ZodCreateUserBodyProd,
  responseBody: ZodCreateUserResponse,
});

export type PostAuthCreateUserEndpoint = GetEndpointType<typeof postAuthCreateUserEndpoint>;
